import {EventEmitter, Injectable} from '@angular/core';
import {ApplicationStateService} from '../../../shared/services/application-state.service';
import {Offer} from '../../models/offer/offer.model';
import {InsuranceCompanyCode} from '../../../shared/models/insurance-company-code.enum';
import {BusinessParamName} from '../../../shared/models/request-offer/request-offer.enum';
import {OfferResourceService} from './offer-resource.service';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {OfferCoveragesService} from './offer-coverages.service';
import {OfferPrimaryCoverageCode} from '../../consts/offer-primary-coverage-code.enum';
import * as _ from 'lodash';
import {OffersTableSelectEvent} from '../../../shared/sections/web/next-offers-section/offers-table/offers-table-cell/offers-table-cell.component';
import {NextOffersSectionStateService} from './section-services/next-offers/next-offers-section-state.service';
import {SortOrder} from '../../../shared/enums/sort-order.enum';

@Injectable({
  providedIn: 'root'
})
export class OffersTableService {

  selectedOffer: Offer;
  offerSelected$: EventEmitter<void> = new EventEmitter<void>();
  private selectedOfferColumnKey: OffersTableColumnKey;
  private expandedInsuranceCompany: InsuranceCompanyCode;
  private tableColumnsConfig: OffersTableColumnConfig[] = [
    {
      key: OffersTableColumnKey.CHEAPEST_OC,
      caption: 'OFFERS_TABLE.cheapestOc'
    },
    {
      key: OffersTableColumnKey.OC_NNW,
      caption: 'OFFERS_TABLE.ocNnw'
    },
    {
      key: OffersTableColumnKey.OC_NNW_ASSISTANCE,
      caption: 'OFFERS_TABLE.ocNnwAssistance'
    },
    {
      key: OffersTableColumnKey.OC_AC,
      caption: 'OFFERS_TABLE.ocAc'
    }
  ];

  private readonly DEFAULT_SORT_ORDER: SortOrder = SortOrder.ASCENDING;

  private sortState: OffersTableSortState = {
    key: OffersTableColumnKey.CHEAPEST_OC,
    order: SortOrder.ASCENDING
  };

  get isTableView(): boolean {
    return this.offersSectionStateService.isTableView;
  }

  constructor(private stateService: ApplicationStateService,
              private offersSectionStateService: NextOffersSectionStateService,
              private offerCoveragesService: OfferCoveragesService,
              private offerResourceService: OfferResourceService) { }

  getColumns(): OffersTableColumnConfig[] {
    return this.tableColumnsConfig.filter(config => !isNotNullOrUndefined(config.disabled) || !config.disabled);
  }

  createTableOffersData(offers: Offer[]): InsuranceCompanyTableOffers[] {
    const data: InsuranceCompanyTableOffers[] = [];
    Object.values(InsuranceCompanyCode).forEach(icCode => {
      const icOffers = this.offerCoveragesService.getInsuranceCompanyOffers(offers, icCode);
      if (icOffers.length > 0) {
        data.push({
          insuranceCompanyCode: icCode,
          logoUri: this.offerResourceService.getOfferLogoUri(icOffers[0]),
          offers: icOffers,
          tableOffers: this.getColumns().map(columnConfig => ({
            key: columnConfig.key,
            offer: this.getCheapestOfferForColumn(columnConfig.key, icOffers)
          }))
        })
      }
    });

    return this.sortTableData(data, this.sortState);
  }

  isOfferSelected(offer: Offer, columnKey: OffersTableColumnKey): boolean {
    if (!!this.selectedOffer && !!offer && this.selectedOfferColumnKey === columnKey) {
      return this.selectedOffer.id === offer.id;
    }
    return false;
  }

  selectOffer(event: OffersTableSelectEvent): void {
    const { offer, columnKey } = event;
    if (offer) {
      this.selectedOffer = offer;
      this.selectedOfferColumnKey = columnKey;
      this.offerSelected$.emit();
    }
  }

  confirmSelectedOffer(): void {
    if (this.selectedOffer) {
      this.offersSectionStateService.selectOffer(this.selectedOffer);
      this.selectedOffer = null;
      this.selectedOfferColumnKey = null;
    }
  }

  changeExpandedInsuranceCompany(code: InsuranceCompanyCode): void {
    this.expandedInsuranceCompany = code;
  }

  isInsuranceCompanyExpanded(code: InsuranceCompanyCode): boolean {
    return this.expandedInsuranceCompany === code;
  }

  isSortActive(columnKey: OffersTableColumnKey): boolean {
    return this.sortState.key === columnKey;
  }

  getSortOrder(columnKey: OffersTableColumnKey): SortOrder | undefined {
    return this.isSortActive(columnKey) ? this.sortState.order : this.DEFAULT_SORT_ORDER;
  }

  updateSort(sortState: OffersTableSortState): void {
    this.sortState = sortState;
  }

  sortTableData(data: InsuranceCompanyTableOffers[], sortState: OffersTableSortState = this.sortState): InsuranceCompanyTableOffers[] {
    return data.sort((item1, item2) => {
      const offer1 = item1.tableOffers.find(tableOffer => tableOffer.key === sortState.key);
      const offer2 = item2.tableOffers.find(tableOffer => tableOffer.key === sortState.key);
      if (isNotNullOrUndefined(offer1.offer) && isNotNullOrUndefined(offer2.offer)) {
        switch (sortState.order) {
          case SortOrder.ASCENDING:
            return Offer.getOfferLowestPrice(offer1.offer) - Offer.getOfferLowestPrice(offer2.offer);
          case SortOrder.DESCENDING:
            return Offer.getOfferLowestPrice(offer2.offer) - Offer.getOfferLowestPrice(offer1.offer);
        }
      } else {
        if (!isNotNullOrUndefined(offer1.offer)) {
          return 1;
        }
        if (!isNotNullOrUndefined(offer2.offer)) {
          return -1;
        }
      }
    });
  }

  private getCheapestOfferForColumn(columnKey: OffersTableColumnKey, offers: Offer[]): Offer {
    let filteredOffers: Offer[];
    switch (columnKey) {
      case OffersTableColumnKey.CHEAPEST_OC:
        filteredOffers = offers;
        break;
      case OffersTableColumnKey.OC_NNW:
        filteredOffers = this.offerCoveragesService.getOffersWithExactPrimaryCoverages(offers, [OfferPrimaryCoverageCode.OC, OfferPrimaryCoverageCode.NNW]);
        break;
      case OffersTableColumnKey.OC_NNW_ASSISTANCE:
        filteredOffers = this.offerCoveragesService.getOffersWithExactPrimaryCoverages(offers, [OfferPrimaryCoverageCode.OC, OfferPrimaryCoverageCode.NNW, OfferPrimaryCoverageCode.ASSISTANCE]);
        break;
      case OffersTableColumnKey.OC_AC:
        filteredOffers = _.uniqBy([
            ...this.offerCoveragesService.getOffersWithPrimaryCoverages(offers, [OfferPrimaryCoverageCode.OC, OfferPrimaryCoverageCode.AC]),
            ...this.offerCoveragesService.getOffersWithPrimaryCoverages(offers, [OfferPrimaryCoverageCode.OC, OfferPrimaryCoverageCode.AC_MINI]),
        ], 'id');
        break;
    }
    return filteredOffers ? this.offerCoveragesService.getCheapestOffer(filteredOffers) : null;
  }
}

export interface InsuranceCompanyTableOffers {
  insuranceCompanyCode: InsuranceCompanyCode;
  logoUri: string;
  offers: Offer[];
  tableOffers: OffersTableColumnOffer[];
}

export interface OffersTableColumnOffer {
  key: OffersTableColumnKey;
  offer: Offer;
}

export enum OffersTableColumnKey {
  CHEAPEST_OC = 'CHEAPEST_OC',
  OC_NNW = 'OC_NNW',
  OC_NNW_ASSISTANCE = 'OC_NNW_ASSISTANCE',
  OC_AC = 'OC_AC',
}

export interface OffersTableColumnConfig {
  key: OffersTableColumnKey;
  caption: string;
  disabled?: boolean;
}

export interface OffersTableSortState {
  key: OffersTableColumnKey;
  order: SortOrder;
}

